import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { isMagnifierActiveSelector } from '@/redux/magnifier/magnifier.selectors';
import { useMapInstance } from '@/utils/context/mapInstanceContext';
import { useMapSources } from '@/utils/context/mapSourcesContext';
import { createContentLayers } from '@/components/Map/MapViewer/utils/config/createContentLayers';
import { useOlMapLoupeView } from '@/components/Map/MapViewer/utils/config/useOlMapLoupeView';
import { Collection, Map } from 'ol';
import Layer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import BaseLayer from 'ol/layer/Base';
import RenderEvent from 'ol/render/Event';
import { unByKey } from 'ol/Observable';
import { MutableRefObject, useEffect, useRef } from 'react';
import { LOUPE_CURSOR_OFFSET } from '@/components/Map/MagnifierLoupe/MagnifierLoupe.constants';
import { setLoupeRenderView } from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/utils/loupeRenderView';
import { LAYER_TYPE } from '@/components/Map/MapViewer/MapViewer.constants';
import { layersVisibilityForCurrentModelSelector } from '@/redux/layers/layers.selectors';

interface UseMagnifierLoupeResult {
  /** Outer floating container, positioned at the cursor. */
  containerRef: MutableRefObject<HTMLDivElement | null>;
  /** Inner element the loupe `ol/Map` renders into. */
  targetRef: MutableRefObject<HTMLDivElement | null>;
  isActive: boolean;
}

/**
 * Drives the magnifier loupe: a second, interaction-free `ol/Map` that renders
 * the same shared sources as the main map but at maximum zoom, following the
 * cursor over the primary map. All cursor-driven updates are imperative (no
 * React state per mouse move) to keep it cheap.
 */
export const useMagnifierLoupe = (): UseMagnifierLoupeResult => {
  const isActive = useAppSelector(isMagnifierActiveSelector);
  const { mapInstance } = useMapInstance();
  const sources = useMapSources();
  const view = useOlMapLoupeView();
  const layersVisibility = useAppSelector(layersVisibilityForCurrentModelSelector);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const loupeMapRef = useRef<Map | null>(null);

  // Create the loupe map once, with no controls or interactions (pure follower).
  useEffect(() => {
    if (!targetRef.current || loupeMapRef.current) {
      return undefined;
    }
    const loupeMap = new Map({
      target: targetRef.current,
      controls: new Collection(),
      interactions: new Collection(),
    });
    loupeMapRef.current = loupeMap;

    // Publish the loupe's view for the duration of its render so glyph images
    // anchor to the loupe's center (not the primary map's). `precompose` fires
    // before any layer's prepareFrame (where styles run); `postcompose` after.
    const renderKeys = [
      loupeMap.on('precompose', () => setLoupeRenderView(loupeMap.getView())),
      loupeMap.on('postcompose', () => setLoupeRenderView(null)),
    ];

    return () => {
      renderKeys.forEach(unByKey);
      setLoupeRenderView(null);
      loupeMap.setTarget(undefined);
      loupeMapRef.current = null;
    };
  }, []);

  // Keep the loupe view in sync (recreated when the diagram/size changes).
  useEffect(() => {
    loupeMapRef.current?.setView(view);
  }, [view]);

  // Build a fresh layer stack over the shared sources for the loupe map.
  // The loupe renders image glyphs at maximum zoom, i.e. small source images
  // upscaled enormously. Bilinear upscaling of those bitmaps every frame is the
  // dominant cost; disabling image smoothing (nearest-neighbour) for the loupe's
  // render makes it far cheaper. Restored after each layer so it doesn't leak.
  useEffect(() => {
    const loupeMap = loupeMapRef.current;
    if (!loupeMap) {
      return undefined;
    }
    // Read-only copies of the user-drawn (additional) layers: new layer objects
    // over the main map's shared sources (an OL layer can't belong to two maps),
    // mirroring z-index and visibility. No editing interactions are attached.
    const additionalLayers = sources.additionalLayers.map(mainLayer => {
      const layerId = mainLayer.get('id');
      const visible =
        layersVisibility[layerId] !== undefined
          ? layersVisibility[layerId]
          : mainLayer.getVisible();
      const layer = new VectorLayer({
        source: mainLayer.getSource() ?? undefined,
        zIndex: mainLayer.getZIndex(),
        visible,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      });
      layer.set('type', LAYER_TYPE.ADDITIONAL_LAYER);
      return layer;
    });
    const layers: BaseLayer[] = [...createContentLayers(sources), ...additionalLayers];
    const disableSmoothing = (event: RenderEvent): void => {
      const context = event.context as CanvasRenderingContext2D | undefined;
      if (context) {
        context.imageSmoothingEnabled = false;
      }
    };
    const restoreSmoothing = (event: RenderEvent): void => {
      const context = event.context as CanvasRenderingContext2D | undefined;
      if (context) {
        context.imageSmoothingEnabled = true;
      }
    };
    const keys = layers.flatMap(layer => [
      (layer as Layer).on('prerender', disableSmoothing),
      (layer as Layer).on('postrender', restoreSmoothing),
    ]);
    loupeMap.setLayers(layers);

    return () => keys.forEach(unByKey);
  }, [sources, layersVisibility]);

  // Set the loupe to the diagram's maximum zoom level once. The resolution never
  // changes, so re-asserting it on every pointermove is wasteful and — more
  // importantly — a resolution change forces OpenLayers to fully re-render the
  // layer (re-running drawImageOrLabel on the upscaled glyphs) instead of letting
  // a center-only pan reuse the already-rendered canvas.
  useEffect(() => {
    if (!mapInstance) {
      return;
    }
    const primaryView = mapInstance.getView();
    const maxZoomResolution = primaryView.getResolutionForZoom(primaryView.get('originalMaxZoom'));
    // Only set it when it actually changes — OpenLayers re-renders on setResolution
    // even if the value is identical.
    if (maxZoomResolution && view.getResolution() !== maxZoomResolution) {
      view.setResolution(maxZoomResolution);
    }
  }, [mapInstance, view]);

  // Follow the cursor over the primary map.
  useEffect(() => {
    if (!mapInstance) {
      return undefined;
    }

    const moveKey = mapInstance.on('pointermove', event => {
      const container = containerRef.current;
      if (!container || !isActive || event.dragging) {
        return;
      }
      view.setCenter(event.coordinate);
      const { clientX, clientY } = event.originalEvent as MouseEvent;
      container.style.left = `${clientX + LOUPE_CURSOR_OFFSET}px`;
      container.style.top = `${clientY + LOUPE_CURSOR_OFFSET}px`;
      container.style.visibility = 'visible';
      // No updateSize() here: the loupe is a fixed size and only needs resizing
      // on activation. Calling it per pointermove forces a layout reflow and an
      // out-of-band repaint on every event; letting OpenLayers coalesce the
      // view change to one render per animation frame keeps it fluent (notably
      // on image-glyph maps like the overview, where each repaint is costly).
    });

    const hideLoupe = (): void => {
      if (containerRef.current) {
        containerRef.current.style.visibility = 'hidden';
      }
    };

    const viewport = mapInstance.getViewport();
    viewport.addEventListener('pointerleave', hideLoupe);

    return () => {
      unByKey(moveKey);
      viewport.removeEventListener('pointerleave', hideLoupe);
    };
  }, [mapInstance, view, isActive]);

  // Toggle display and refresh size when (de)activated.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.style.display = isActive ? 'block' : 'none';
    if (isActive) {
      loupeMapRef.current?.updateSize();
    }
  }, [isActive]);

  return { containerRef, targetRef, isActive };
};
